/* ═══════════════════════════════════════════════════════════════
   3D-Ansicht der Flasche — eigener kleiner WebGL-Zeichner

   Warum kein Three.js: Gezeichnet wird genau eine Sorte Körper, ein
   Drehkörper mit einer Bildhaut, drei Teile, ein Licht-Aufbau. Das
   sind ein Shader-Paar und ein paar Matrizen — gut 300 Zeilen statt
   eines Pakets mit einem Vielfachen der heutigen app.js (und wie
   beim QR-Code und beim PDF-Schreiber: keine neue Abhängigkeit).
   WebGL 1, damit es auch auf älteren Telefonen läuft.

   Licht: Die Haut stammt aus der Aufnahme und bringt ihr eigenes
   Licht mit — echte Spiegelungen und Glanz auf Glas und Kapsel sind
   schon im Bild. Gezeichnet wird darum nur eine gedämpfte Schattierung
   (Hauptlicht von links oben, schwächeres von rechts), die die Rundung
   lesbar macht, und kein künstlicher Glanz: Ein zusätzlicher
   Lichtstreifen lag sonst als zweite, fremde Spiegelung über der
   echten.

   Werkstoffe je Teil:
   - Glas, Folie, Schraubverschluss: Haut aus der Aufnahme (Aufdrucke
     auf der Kapsel bleiben erhalten).
   - Wachs: einfarbig in der gemessenen Kapselfarbe — die modellierten
     Wachsnasen sollen nicht mit den fotografierten um die Wette
     laufen.
   - Deckel: einfarbig in der Kapselfarbe.

   Vergrößern: `zeichne({ zoom, versatz })` vergrößert das fertige Bild
   um einen Punkt (wie eine Lupe, in Bildkoordinaten −1 … 1); die
   Flasche wird dabei neu gezeichnet, also scharf bis zur Auflösung der
   Haut.
   ═══════════════════════════════════════════════════════════════ */

import { flaschenTeile, formVon, koerperRadius } from "../kern/flasche.js";


const ECKEN = `
attribute vec3 aPos;
attribute vec3 aNormale;
attribute vec2 aUv;
uniform mat4 uModell;
uniform mat4 uAnsicht;
uniform mat4 uProjektion;
varying vec3 vNormale;
varying vec3 vPos;
varying vec2 vUv;
void main() {
  vec4 welt = uAnsicht * uModell * vec4(aPos, 1.0);
  vPos = welt.xyz;
  vNormale = mat3(uAnsicht * uModell) * aNormale;
  vUv = aUv;
  gl_Position = uProjektion * welt;
}`;

const FLAECHEN = `
precision mediump float;
varying vec3 vNormale;
varying vec3 vPos;
varying vec2 vUv;
uniform sampler2D uHaut;
uniform vec3 uFarbe;
uniform float uFarbAnteil;
void main() {
  vec3 n = normalize(vNormale);
  if (!gl_FrontFacing) n = -n;
  vec3 basis = mix(texture2D(uHaut, vUv).rgb, uFarbe, uFarbAnteil);
  vec3 l1 = normalize(vec3(-0.55, 0.55, 0.75));
  vec3 l2 = normalize(vec3(0.75, 0.15, 0.45));
  float diffus = 0.42 + 0.5 * max(dot(n, l1), 0.0) + 0.2 * max(dot(n, l2), 0.0);
  gl_FragColor = vec4(min(basis * mix(1.0, diffus, 0.6), vec3(1.0)), 1.0);
}`;

/* Anteil der Kapselfarbe statt der Haut je Kapselart */
const FARBANTEIL = { folie: 0, schraub: 0, wachs: 1 };


/* ── Matrizen (spaltenweise, wie WebGL sie erwartet) ─────────── */
function perspektive(fov, seitenverhaeltnis, nah, fern) {
  const f = 1 / Math.tan(fov / 2), nf = 1 / (nah - fern);
  return new Float32Array([f / seitenverhaeltnis, 0, 0, 0, 0, f, 0, 0, 0, 0, (fern + nah) * nf, -1, 0, 0, 2 * fern * nah * nf, 0]);
}
function mal(a, b) {
  const o = new Float32Array(16);
  for (let s = 0; s < 4; s++) for (let z = 0; z < 4; z++) {
    let summe = 0;
    for (let k = 0; k < 4; k++) summe += a[k * 4 + z] * b[s * 4 + k];
    o[s * 4 + z] = summe;
  }
  return o;
}
const verschiebung = (x, y, z) => new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]);
const drehungY = (w) => { const c = Math.cos(w), s = Math.sin(w); return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]); };
const drehungX = (w) => { const c = Math.cos(w), s = Math.sin(w); return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]); };


function shader(gl, art, quelle) {
  const s = gl.createShader(art);
  gl.shaderSource(s, quelle);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "Shader");
  return s;
}


/** WebGL vorhanden? Ohne zeigt die Oberfläche die flache Abwicklung. */
function webglMoeglich() {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl") || c.getContext("experimental-webgl"));
  } catch (e) { return false; }
}


/**
 * Zeichner an eine Leinwand binden. Liefert null ohne WebGL.
 *
 * optionen: { form, kapsel: { art, anteil, farbe: [r,g,b] }, beiVerlust }
 * Rückgabe: { setzeHaut(bildquelle), setzeModell({ form, kapsel }),
 *             zeichne({ winkel, neigung, zoom, versatz }), entsorgen() }
 */
function flascheDarstellen(leinwand, optionen = {}) {
  let gl = null;
  try {
    const attr = { antialias: true, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: false };
    gl = leinwand.getContext("webgl", attr) || leinwand.getContext("experimental-webgl", attr);
  } catch (e) { gl = null; }
  if (!gl) return null;

  const programm = gl.createProgram();
  gl.attachShader(programm, shader(gl, gl.VERTEX_SHADER, ECKEN));
  gl.attachShader(programm, shader(gl, gl.FRAGMENT_SHADER, FLAECHEN));
  gl.linkProgram(programm);
  if (!gl.getProgramParameter(programm, gl.LINK_STATUS)) return null;
  gl.useProgram(programm);
  const ort = (n) => gl.getUniformLocation(programm, n);
  const u = {
    modell: ort("uModell"), ansicht: ort("uAnsicht"), projektion: ort("uProjektion"),
    haut: ort("uHaut"), farbe: ort("uFarbe"), farbAnteil: ort("uFarbAnteil"),
  };
  const a = {
    pos: gl.getAttribLocation(programm, "aPos"),
    normale: gl.getAttribLocation(programm, "aNormale"),
    uv: gl.getAttribLocation(programm, "aUv"),
  };
  const uint32 = !!gl.getExtension("OES_element_index_uint");

  /* Platzhalterhaut: dunkles Flaschengrün, bis das Bild geladen ist. */
  const haut = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, haut);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([34, 52, 38, 255]));
  for (const [k, v] of [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR],
    [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]]) gl.texParameteri(gl.TEXTURE_2D, k, v);

  let teile = [];
  let form = formVon(optionen.form);
  let kapsel = optionen.kapsel || {};
  let verloren = false;

  const puffer = (daten, ziel = gl.ARRAY_BUFFER) => {
    const b = gl.createBuffer();
    gl.bindBuffer(ziel, b);
    gl.bufferData(ziel, daten, gl.STATIC_DRAW);
    return b;
  };

  function baueTeile() {
    for (const t of teile) for (const b of [t.pos, t.normale, t.uv, t.idx]) gl.deleteBuffer(b);
    const art = kapsel.art || "folie";
    const netze = flaschenTeile(form, { kapselAnteil: kapsel.anteil != null ? kapsel.anteil : form.kapsel, art });
    const farbe = (kapsel.farbe || [120, 110, 100]).map((c) => c / 255);
    const kapselAnteil = FARBANTEIL[art] ?? 0;
    teile = [];
    for (const [name, netz] of Object.entries(netze)) {
      if (!netz) continue;
      const indizes = netz.indizes;
      if (indizes instanceof Uint32Array && !uint32) continue;
      teile.push({
        pos: puffer(netz.positionen), normale: puffer(netz.normalen), uv: puffer(netz.uv),
        idx: puffer(indizes, gl.ELEMENT_ARRAY_BUFFER), anzahl: indizes.length,
        typ: indizes instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT,
        farbAnteil: name === "glas" ? 0 : name === "deckel" ? 1 : kapselAnteil, farbe,
      });
    }
  }
  baueTeile();

  function setzeHaut(quelle) {
    if (!quelle || verloren) return;
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048;
    let bild = quelle;
    const b = quelle.naturalWidth || quelle.width, h = quelle.naturalHeight || quelle.height;
    if (b > max || h > max) {
      const f = max / Math.max(b, h);
      const c = document.createElement("canvas");
      c.width = Math.round(b * f); c.height = Math.round(h * f);
      c.getContext("2d").drawImage(quelle, 0, 0, c.width, c.height);
      bild = c;
    }
    gl.bindTexture(gl.TEXTURE_2D, haut);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bild);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  }

  function setzeModell(neu) {
    if (verloren) return;
    if (neu.form) form = formVon(neu.form);
    if (neu.kapsel) kapsel = neu.kapsel;
    baueTeile();
  }

  function zeichne({ winkel = 0, neigung = 0.1, zoom = 1, versatz = [0, 0] } = {}) {
    if (verloren) return;
    const pr = Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1);
    const b = Math.max(1, Math.round(leinwand.clientWidth * pr)), h = Math.max(1, Math.round(leinwand.clientHeight * pr));
    if (leinwand.width !== b || leinwand.height !== h) { leinwand.width = b; leinwand.height = h; }
    gl.viewport(0, 0, b, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);

    /* Abstand so, dass die ganze Flasche ins Bild passt — je nachdem,
       ob die Leinwand eher hoch oder eher breit ist. */
    const fov = 0.42;
    const verhaeltnis = b / h;
    const H = form.hoehe + 12, B = 2 * koerperRadius(form) + 12;
    const halbHoch = Math.max(H / 2, (B / 2) / verhaeltnis) * 1.06;
    const abstand = halbHoch / Math.tan(fov / 2) + koerperRadius(form);
    /* Lupe: Bildkoordinaten x' = zoom · x + versatz */
    const lupe = new Float32Array([zoom, 0, 0, 0, 0, zoom, 0, 0, 0, 0, 1, 0, versatz[0], versatz[1], 0, 1]);
    const projektion = mal(lupe, perspektive(fov, verhaeltnis, abstand * 0.3, abstand * 3));
    const ansicht = mal(verschiebung(0, 0, -abstand), drehungX(neigung));
    const modell = mal(drehungY(winkel), verschiebung(0, -form.hoehe / 2 - (kapsel.art === "ohne" ? 0 : 2), 0));
    gl.uniformMatrix4fv(u.projektion, false, projektion);
    gl.uniformMatrix4fv(u.ansicht, false, ansicht);
    gl.uniformMatrix4fv(u.modell, false, modell);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, haut);
    gl.uniform1i(u.haut, 0);

    for (const t of teile) {
      gl.uniform3fv(u.farbe, t.farbe);
      gl.uniform1f(u.farbAnteil, t.farbAnteil);
      for (const [ortA, buf, n] of [[a.pos, t.pos, 3], [a.normale, t.normale, 3], [a.uv, t.uv, 2]]) {
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.enableVertexAttribArray(ortA);
        gl.vertexAttribPointer(ortA, n, gl.FLOAT, false, 0, 0);
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, t.idx);
      gl.drawElements(gl.TRIANGLES, t.anzahl, t.typ, 0);
    }
  }

  const verlust = (e) => {
    e.preventDefault();
    verloren = true;
    if (optionen.beiVerlust) optionen.beiVerlust();
  };
  leinwand.addEventListener("webglcontextlost", verlust);

  function entsorgen() {
    leinwand.removeEventListener("webglcontextlost", verlust);
    if (verloren) return;
    for (const t of teile) for (const b of [t.pos, t.normale, t.uv, t.idx]) gl.deleteBuffer(b);
    gl.deleteTexture(haut);
    gl.deleteProgram(programm);
    /* Telefone erlauben nur eine Handvoll Kontexte gleichzeitig —
       freigeben statt auf die Speicherbereinigung zu warten. */
    const ext = gl.getExtension("WEBGL_lose_context");
    if (ext) ext.loseContext();
    verloren = true;
  }

  return { setzeHaut, setzeModell, zeichne, entsorgen };
}


export { flascheDarstellen, webglMoeglich };
